import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { Modal } from '@/components/modal';
import { ModalProps } from '@mui/base/Modal';
import { I18N_LANGUAGES } from '@/i18n';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { Editor } from '@tinymce/tinymce-react';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import DOMPurify from 'dompurify';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';

type TranslationMap = Record<
  string,
  Partial<{ name: string; description: string; name_sub_title: string }>
>;

interface BaseSubFormData {
  name?: string;
  name_sub_title?: string;
  description?: string;
  // Can be stringified JSON or a map — kept for backwards-compat but
  // prefer passing `initialTranslations` as a parsed object.
  translations?: string | TranslationMap;
}

interface SubscriptionPreviewProps extends Omit<ModalProps, 'children'> {
  formData: Partial<BaseSubFormData>;
  source_language?: string;
  /** Pre-existing translations from the API (edit mode). When provided the
   *  modal skips the auto-fetch and uses these directly. */
  initialTranslations?: TranslationMap;
  onTranslationsGenerated?: (translations: TranslationMap) => void;
  /** Called when the user edits the source-language card so the parent form
   *  stays in sync. */
  onSourceFormDataChange?: (fields: {
    name?: string;
    name_sub_title?: string;
    description?: string;
  }) => void;
  className?: string;
}

interface EditValues {
  name?: string;
  name_sub_title?: string;
  description?: string;
}

interface LanguagePreviewProps {
  language: { code: string; label: string; flag: string };
  sourceLanguage: string;
  formData: Partial<BaseSubFormData>;
  translatedContent?: TranslationMap;
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => void;
  onSourceChange?: (fields: Partial<EditValues>) => void;
}

// Pure helper — hoisted to module scope so it is not recreated per render
const htmlToPlainText = (html?: string): string => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  sourceLanguage,
  formData,
  translatedContent,
  isLoading,
  onEditContent,
  onSourceChange,
}) => {
  const intl = useIntl();
  const isSource = language.code === sourceLanguage;
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>({});

  const langContent = useMemo(() => {
    if (isSource) {
      return {
        name: formData.name,
        name_sub_title: formData.name_sub_title,
        description: formData.description,
      };
    }
    return translatedContent && translatedContent[language.code]
      ? translatedContent[language.code]
      : {
          name: formData.name,
          name_sub_title: formData.name_sub_title,
          description: formData.description,
        };
  }, [
    isSource,
    language.code,
    formData.name,
    formData.name_sub_title,
    formData.description,
    translatedContent,
  ]);

  const toggleEditMode = () => {
    if (!isEditing) {
      const initial: EditValues = {};
      if (langContent?.name) initial.name = htmlToPlainText(langContent.name);
      if (langContent?.name_sub_title)
        initial.name_sub_title = htmlToPlainText(langContent.name_sub_title);
      if (langContent?.description)
        initial.description = langContent.description;
      setEditValues(initial);
    } else {
      setEditValues({});
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (field: keyof EditValues, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    // For source language, propagate changes back to the parent form in real-time
    if (isSource && onSourceChange) {
      onSourceChange({ [field]: value });
    }
  };

  const handleSave = () => {
    if (isSource) {
      if (onSourceChange) {
        onSourceChange(editValues);
      }
      setIsEditing(false);
      return;
    }
    if (onEditContent) {
      Object.entries(editValues).forEach(([field, value]) => {
        onEditContent(
          language.code,
          field as keyof EditValues,
          value as string
        );
      });
    }
    // Always exit edit mode regardless of whether onEditContent is defined
    setIsEditing(false);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={language.flag} alt={language.label} className="w-4 h-4" />
          <span className="font-medium">{language.label}</span>
          {isSource && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {intl.formatMessage({ id: 'SUBSCRIPTION.PREVIEW.SOURCE_LABEL' })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-xs btn-light"
            onClick={toggleEditMode}
            disabled={isLoading}
          >
            {isEditing
              ? intl.formatMessage({
                  id: 'SUBSCRIPTION.PREVIEW.BUTTON.CANCEL',
                })
              : intl.formatMessage({
                  id: 'SUBSCRIPTION.PREVIEW.BUTTON.EDIT',
                })}
          </button>
          {isEditing && (
            <button
              type="button"
              className="btn btn-xs btn-primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {intl.formatMessage({ id: 'SUBSCRIPTION.PREVIEW.BUTTON.SAVE' })}
            </button>
          )}
        </div>
      </div>

      <div className="p-4">
        {isLoading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              {isEditing ? (
                <label
                  htmlFor={`name-${language.code}`}
                  className="text-xs uppercase text-gray-500 mb-1 block"
                >
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.PREVIEW.FIELD.NAME',
                  })}
                </label>
              ) : (
                <span className="text-xs uppercase text-gray-500 mb-1 block">
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.PREVIEW.FIELD.NAME',
                  })}
                </span>
              )}

              {isEditing ? (
                <input
                  id={`name-${language.code}`}
                  type="text"
                  value={
                    editValues.name ?? htmlToPlainText(langContent.name) ?? ''
                  }
                  autoComplete="off"
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input input-bordered w-full"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {htmlToPlainText(langContent?.name) || '—'}
                </p>
              )}
            </div>

            <div>
              {isEditing ? (
                <label
                  htmlFor={`subtitle-${language.code}`}
                  className="text-xs uppercase text-gray-500 mb-1 block"
                >
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.PREVIEW.FIELD.SUBTITLE',
                  })}
                </label>
              ) : (
                <span className="text-xs uppercase text-gray-500 mb-1 block">
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.PREVIEW.FIELD.SUBTITLE',
                  })}
                </span>
              )}

              {isEditing ? (
                <input
                  id={`subtitle-${language.code}`}
                  type="text"
                  value={
                    editValues.name_sub_title ??
                    langContent.name_sub_title ??
                    ''
                  }
                  autoComplete="off"
                  onChange={(e) =>
                    handleChange('name_sub_title', e.target.value)
                  }
                  className="input input-bordered w-full"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {langContent?.name_sub_title || '—'}
                </p>
              )}
            </div>

            <div>
              {isEditing ? (
                <label
                  htmlFor={`description-${language.code}`}
                  className="text-xs uppercase text-gray-500 mb-1 block"
                >
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.PREVIEW.FIELD.DESCRIPTION',
                  })}
                </label>
              ) : (
                <span className="text-xs uppercase text-gray-500 mb-1 block">
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.PREVIEW.FIELD.DESCRIPTION',
                  })}
                </span>
              )}

              {isEditing ? (
                <Editor
                  tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                  value={
                    editValues.description ?? langContent.description ?? ''
                  }
                  init={tinymceInit}
                  onEditorChange={(content: string) =>
                    handleChange('description', content)
                  }
                  textareaName={`description-${language.code}`}
                />
              ) : (
                <div className="prose prose-sm max-w-none">
                  {langContent?.description ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(langContent.description),
                      }}
                      className="text-gray-900"
                    />
                  ) : (
                    <p className="text-sm text-gray-500">—</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SubscriptionPreviewModal: React.FC<SubscriptionPreviewProps> = ({
  open,
  onClose,
  formData,
  source_language = 'en',
  initialTranslations,
  onTranslationsGenerated,
  onSourceFormDataChange,
  className,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  // Parse translations from formData.translations (string | object) as a
  // fallback when initialTranslations is not provided.
  const parseFormDataTranslations = (): TranslationMap => {
    if (!formData.translations) return {};
    if (typeof formData.translations === 'string') {
      try {
        return JSON.parse(formData.translations) as TranslationMap;
      } catch {
        return {};
      }
    }
    return formData.translations as TranslationMap;
  };

  const [translations, setTranslations] = useState<TranslationMap>(
    () => initialTranslations ?? parseFormDataTranslations()
  );

  const currentFormDataRef = useRef(formData);
  const currentSourceLanguageRef = useRef(source_language);
  const currentCallbackRef = useRef(onTranslationsGenerated);

  useEffect(() => {
    currentFormDataRef.current = formData;
    currentSourceLanguageRef.current = source_language;
    currentCallbackRef.current = onTranslationsGenerated;
  }, [formData, source_language, onTranslationsGenerated]);

  // Sync initialTranslations whenever the parent updates them (e.g. on re-open or after an edit).
  useEffect(() => {
    if (initialTranslations && Object.keys(initialTranslations).length > 0) {
      setTranslations(initialTranslations);
    }
  }, [initialTranslations]);

  const fetchTranslations = useCallback(async () => {
    const fd = currentFormDataRef.current;
    if (!fd.name) {
      enqueueSnackbar('Please provide a name to translate', {
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await SubscriptionsService.translateSubscriptionContent({
        name: fd.name,
        name_sub_title: fd.name_sub_title,
        description: fd.description,
        source_language: currentSourceLanguageRef.current,
      });

      setTranslations(result);
      currentCallbackRef.current?.(result);
      if (result.warning_message) {
        enqueueSnackbar(result.warning_message, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Translations generated successfully', {
          variant: 'success',
        });
      }
    } catch (error: unknown) {
      const e = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        e?.response?.data?.message || e?.message || 'Translation failed';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  // Reset loading state when modal closes
  useEffect(() => {
    if (!open) {
      setIsLoading(false);
    }
  }, [open]);

  const handleEditContent = (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => {
    setTranslations((prev) => ({
      ...prev,
      [languageCode]: {
        ...(prev[languageCode] || {}),
        [field]: value,
      },
    }));
  };

  const handleSourceChange = (fields: Partial<EditValues>) => {
    if (onSourceFormDataChange) {
      onSourceFormDataChange(fields);
    }
  };

  const handleApply = () => {
    const cb = currentCallbackRef.current;
    if (cb) cb(translations);
    enqueueSnackbar(
      intl.formatMessage({ id: 'SUBSCRIPTION.PREVIEW.TRANSLATIONS_UPDATED' }),
      { variant: 'success' }
    );
    onClose?.({}, 'backdropClick');
  };

  const handleAutoFillFromSource = () => {
    const sourceLang = currentSourceLanguageRef.current;
    const srcName = (currentFormDataRef.current.name as string) || '';
    const srcSub = (currentFormDataRef.current.name_sub_title as string) || '';
    const srcDesc = (currentFormDataRef.current.description as string) || '';
    const filled: TranslationMap = { ...translations };
    I18N_LANGUAGES.forEach((lang) => {
      if (lang.code !== sourceLang) {
        filled[lang.code] = {
          ...filled[lang.code],
          name: filled[lang.code]?.name || srcName,
          name_sub_title: filled[lang.code]?.name_sub_title || srcSub,
          description: filled[lang.code]?.description || srcDesc,
        };
      }
    });
    setTranslations(filled);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
    >
      <div
        className="subscription-preview-modal bg-gray-100 w-full max-w-5xl mx-4 rounded-lg shadow-2xl flex flex-col p-6 max-h-[97dvh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-preview-title"
        aria-describedby="subscription-preview-desc"
      >
        <div className="flex items-center justify-between mb-4">
          <h1
            id="subscription-preview-title"
            className="text-lg font-medium text-gray-900"
          >
            {intl.formatMessage({ id: 'SUBSCRIPTION.PREVIEW.TITLE' })}
          </h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={handleAutoFillFromSource}
              disabled={isLoading}
            >
              {intl.formatMessage({
                id: 'SUBSCRIPTION.PREVIEW.BUTTON.AUTO_FILL',
              })}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={fetchTranslations}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin mr-1 h-3 w-3"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {intl.formatMessage({
                    id: 'SUBSCRIPTION.MODAL.BUTTON.PREPARING_PREVIEW',
                  })}
                </>
              ) : (
                intl.formatMessage({
                  id: 'SUBSCRIPTION.PREVIEW.BUTTON.REGENERATE',
                  defaultMessage: 'Regenerate Translations',
                })
              )}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onClose?.({}, 'backdropClick')}
            >
              {intl.formatMessage({ id: 'SUBSCRIPTION.PREVIEW.BUTTON.CLOSE' })}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleApply}
              disabled={isLoading}
            >
              {intl.formatMessage({ id: 'SUBSCRIPTION.PREVIEW.BUTTON.APPLY' })}
            </button>
          </div>
        </div>

        {/* SR-only description for screen readers */}
        <p id="subscription-preview-desc" className="sr-only">
          Preview and optionally edit translated content for the subscription
          across languages.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {I18N_LANGUAGES.map((lang) => (
            <LanguagePreview
              key={lang.code}
              language={lang}
              sourceLanguage={source_language}
              formData={formData}
              translatedContent={
                translations as Record<
                  string,
                  {
                    name?: string;
                    description?: string;
                    name_sub_title?: string;
                  }
                >
              }
              isLoading={isLoading}
              onEditContent={handleEditContent}
              onSourceChange={handleSourceChange}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionPreviewModal;
